const MIN_NUM_OFF_RAMPS = 1;
const MAX_NUM_ON_RAMPS_AFTER_OFF_RAMPS = 1;
const MAX_DEPTH = 1000;

type SegmentStream = {
  segmentIds: string[];
  numOffRampsAtTruncationSegmentId: number | undefined;
  numOnRampsAtTruncationSegmentId: number | undefined;
};

const recurseDownstream = (
  routingMap,
  reverseRoutingMap,
  curSegmentIds: string[],
  curNumOffRamps: number,
  curNumOnRampsAfterOffRamp: number,
  truncationSegmentId: string = undefined,
  depth = 0,
): SegmentStream => {
  const curSegmentId = curSegmentIds[curSegmentIds.length - 1];

  // End condition: just make sure there are no horrible loops
  if (depth > MAX_DEPTH) {
    return {
      segmentIds: curSegmentIds,
      numOffRampsAtTruncationSegmentId: undefined,
      numOnRampsAtTruncationSegmentId: undefined,
    };
  }

  const childrenFromEndNode = Object.keys(
    routingMap.forward[curSegmentId] || {},
  );

  // Check if endpoint is an off ramp
  if (childrenFromEndNode.length > 1) {
    curNumOffRamps += 1;
  }

  // Start counting on-ramps once we're past the first off ramp
  if (curNumOffRamps >= MIN_NUM_OFF_RAMPS) {
    // Check if endpoint is an on ramp
    // Only need to find one on-ramp
    const endIsOnRamp = childrenFromEndNode.some((endNodeChild) => {
      const parentsFromEndNode = Object.keys(
        reverseRoutingMap.forward[endNodeChild] || {},
      );
      return parentsFromEndNode.length > 1;
    });

    if (endIsOnRamp) {
      curNumOnRampsAfterOffRamp += 1;
    }
  }

  // End condition: hit truncation segment
  if (truncationSegmentId === curSegmentId) {
    return {
      segmentIds: curSegmentIds,
      numOffRampsAtTruncationSegmentId: curNumOffRamps,
      numOnRampsAtTruncationSegmentId: curNumOnRampsAfterOffRamp,
    };
  }

  // End condition: nowhere to go
  if (childrenFromEndNode.length === 0) {
    return {
      segmentIds: curSegmentIds,
      numOffRampsAtTruncationSegmentId: undefined,
      numOnRampsAtTruncationSegmentId: undefined,
    };
  }

  // End condition: hit threshold
  if (
    curNumOffRamps >= MIN_NUM_OFF_RAMPS &&
    curNumOnRampsAfterOffRamp >= MAX_NUM_ON_RAMPS_AFTER_OFF_RAMPS
  ) {
    return {
      segmentIds: curSegmentIds,
      numOffRampsAtTruncationSegmentId: undefined,
      numOnRampsAtTruncationSegmentId: undefined,
    };
  }

  // Recurse on all the children
  return childrenFromEndNode.reduce(
    (acc, childFromEndNode) => {
      const {
        segmentIds,
        numOffRampsAtTruncationSegmentId,
        numOnRampsAtTruncationSegmentId,
      } = recurseDownstream(
        routingMap,
        reverseRoutingMap,
        curSegmentIds.concat(childFromEndNode),
        curNumOffRamps,
        curNumOnRampsAfterOffRamp,
        truncationSegmentId,
        depth + 1,
      );
      acc.segmentIds.push(...segmentIds);
      acc.numOffRampsAtTruncationSegmentId =
        acc.numOffRampsAtTruncationSegmentId ||
        numOffRampsAtTruncationSegmentId;
      acc.numOnRampsAtTruncationSegmentId =
        acc.numOnRampsAtTruncationSegmentId || numOnRampsAtTruncationSegmentId;
      return acc;
    },
    {
      segmentIds: [],
      numOffRampsAtTruncationSegmentId: undefined,
      numOnRampsAtTruncationSegmentId: undefined,
    },
  );
};

export const reverseRouting = (routing) => {
  if (!routing) {
    return undefined;
  }

  const reversed = {};
  const forwardRouting = routing.forward;
  Object.keys(forwardRouting).forEach((segmentId) => {
    const childrenSegmentIds = forwardRouting[segmentId];
    Object.keys(childrenSegmentIds).forEach((childSegmentId) => {
      const edgeCost = childrenSegmentIds[childSegmentId];
      if (reversed[childSegmentId] === undefined) {
        reversed[childSegmentId] = { [segmentId]: edgeCost };
      } else {
        reversed[childSegmentId] = {
          ...reversed[childSegmentId],
          [segmentId]: edgeCost,
        };
      }
    });
  });
  return { forward: reversed };
};

/**
 * From the first selectedSegmentId, return all segments up until
 * the first on-ramp that is past the first off-ramp.
 *
 * @param routingMap
 * @param reverseRoutingMap
 * @param selectedSegmentId
 * @param selectedRoute
 * @returns
 */
const calculateSelectableSegmentIds = (
  routingMap,
  reverseRoutingMap,
  selectedSegmentId: string,
  selectedRoute: string[] | undefined,
): string[] => {
  if (!routingMap || !reverseRoutingMap) {
    return undefined;
  }

  // Off-ramp = multiple children from endpoint
  // On-ramp = multiple parents for endpoint

  if (selectedRoute) {
    // Get the points of the current route
    const {
      numOffRampsAtTruncationSegmentId,
      numOnRampsAtTruncationSegmentId,
    } = recurseDownstream(
      routingMap,
      reverseRoutingMap,
      [selectedRoute[0]], // Start at the start of the current route
      0,
      0,
      selectedRoute[selectedRoute.length - 1], // Truncate at the end of the current route
    );

    // Route from the end of the current route
    const { segmentIds } = recurseDownstream(
      routingMap,
      reverseRoutingMap,
      [selectedRoute[selectedRoute.length - 1]], // Start at the end of the current route
      numOffRampsAtTruncationSegmentId || 0, // Init with the points of the current route
      numOnRampsAtTruncationSegmentId || 0,
    );

    return [...new Set([...selectedRoute, ...segmentIds])];
  }

  if (selectedSegmentId) {
    const { segmentIds } = recurseDownstream(
      routingMap,
      reverseRoutingMap,
      [selectedSegmentId],
      0,
      0,
    );
    return [...new Set(segmentIds)];
  }

  // No selected route, no selected segment
  return undefined;
};

export const segmentLayerFilter = (
  routingMap,
  reverseRoutingMap,
  selectedSegmentId: string,
  selectedRoute: string[] | undefined,
) => {
  // Calculate selectable route
  const selectableSegmentIds = calculateSelectableSegmentIds(
    routingMap,
    reverseRoutingMap,
    selectedSegmentId,
    selectedRoute,
  );

  // No filter
  if (selectableSegmentIds === undefined) {
    return ['get', 'closable'];
  }

  // Filter out the segments that shouldn't be selectable
  return [
    'all',
    ['get', 'closable'],
    [
      'match',
      ['get', 'segment_id'],
      ...(selectableSegmentIds || []).flatMap((segmentId) => [segmentId, true]),
      false,
    ],
  ];
};
